import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { useRef } from 'react'
import { ArrowDown } from 'lucide-react'
import { useEntrance } from '@/components/motion/reveal'
import { SplitChars } from '@/components/motion/split-text'
import PixelPortrait from '@/components/webgl/pixel-portrait'
import Img from '@/components/generals/img'
import { lookbookChapters } from '@/constants/content'
import { media } from '@/constants/media'

const LookbookIntro: FC = (): ReactNode => {
    const root = useRef<HTMLElement>(null)

    useEntrance(root, timeline => {
        timeline
            .from('[data-lb-meta]', { autoAlpha: 0, y: 12, duration: 1 }, 0)
            .from('[data-lb-title] .mask > span', { yPercent: 140, duration: 1.4, stagger: 0.05, ease: 'expo.out' }, 0.05)
            .from('[data-lb-row]', { autoAlpha: 0, y: 24, duration: 1, stagger: 0.08 }, 0.5)
            .from('[data-lb-line]', { scaleX: 0, transformOrigin: '0% 50%', duration: 1.3, stagger: 0.08, ease: 'expo.inOut' }, 0.4)
            .from('[data-lb-portrait]', { clipPath: 'inset(100% 0% 0% 0%)', duration: 1.6, ease: 'expo.inOut' }, 0.1)
    })

    return (
        <section ref={root} aria-labelledby='lookbook-title' className='pb-20 pt-[calc(var(--header-height)+40px)] gutter md:pb-28'>
            <div className='grid gap-12 lg:grid-cols-12 lg:gap-10'>
                <div className='flex flex-col lg:col-span-7'>
                    <p data-lb-meta className='label-mono'>
                        (Lookbook) — SS26 Essentials 04
                    </p>

                    <h1 id='lookbook-title' data-lb-title className='mt-8'>
                        <span className='block font-heading text-[clamp(84px,13vw,210px)] uppercase leading-[0.8]'>
                            <SplitChars text='Look' />
                        </span>

                        <span className='-mt-[0.1em] block pl-[6vw] font-serif text-[clamp(80px,12vw,196px)] italic leading-[0.85] tracking-[-0.03em] text-acid'>
                            <SplitChars text='book' />
                        </span>
                    </h1>

                    <nav aria-label='Chapters' className='mt-12 lg:mt-auto'>
                        <ul>
                            {lookbookChapters.map(chapter => (
                                <li key={chapter.id} className='relative'>
                                    <span data-lb-line aria-hidden='true' className='absolute inset-x-0 top-0 h-px bg-line' />

                                    <Link
                                        href={`#${chapter.id}`}
                                        data-lb-row
                                        className='group flex items-center justify-between gap-6 py-4'
                                    >
                                        <span className='meta-mono text-ash'>
                                            Chapter {chapter.numeral}
                                        </span>

                                        <span className='flex items-center gap-3 label-mono text-bone transition-colors duration-300 group-hover:text-acid'>
                                            {chapter.name}

                                            <ArrowDown aria-hidden='true' className='size-4 transition-transform duration-500 ease-expo-out group-hover:translate-y-1' />
                                        </span>
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </nav>
                </div>

                <figure className='lg:col-span-5'>
                    <div data-lb-portrait className='relative aspect-[4/5] overflow-hidden bg-coal'>
                        <Img src={media.wetHairConcrete} alt="A model with wet hair in a black shell jacket against a concrete wall" sizes='(min-width: 1024px) 40vw, 100vw' preload />

                        <PixelPortrait image={media.wetHairConcrete} className='absolute inset-0' />
                    </div>

                    <figcaption className='mt-3 flex justify-between meta-mono text-ash'>
                        <span>(Portrait) — Look 02</span>
                        <span className='hidden sm:inline'>Move to focus</span>
                    </figcaption>
                </figure>
            </div>
        </section>
    )
}

export default LookbookIntro
